import React, { useCallback } from 'react'
import { Button as BsButton, Form, InputGroup } from 'react-bootstrap'
import { isValidUrl } from '../../../common/urlValidation'
import { type RedirectUri, createRedirectUri } from './redirectUri'

interface RedirectUrisFormFieldsProps {
  uris: RedirectUri[]
  setUris: (updater: (prev: RedirectUri[]) => RedirectUri[]) => void
  validated: boolean
}

export const RedirectUrisFormFields: React.FC<RedirectUrisFormFieldsProps> = ({ uris, setUris, validated }) => {
  const handleAddUri = useCallback(() => {
    setUris((prev) => [...prev, createRedirectUri()])
  }, [setUris])

  const handleRemoveUri = useCallback(
    (id: string) => {
      setUris((prev) => prev.filter((uri) => uri.id !== id))
    },
    [setUris],
  )

  const handleUriChange = useCallback(
    (id: string, value: string) => {
      setUris((prev) => prev.map((uri) => (uri.id === id ? { ...uri, value } : uri)))
    },
    [setUris],
  )

  return (
    <>
      {uris.map((uri) => (
        <InputGroup key={uri.id} className='mb-2'>
          <Form.Control
            type='url'
            placeholder='https://your-app.com/callback'
            value={uri.value}
            onChange={(e) => handleUriChange(uri.id, e.target.value)}
            isInvalid={validated && uri.value.trim() !== '' && !isValidUrl(uri.value)}
            required={uris.length === 1}
          />
          {uris.length > 1 ? (
            <BsButton variant='outline-secondary' onClick={() => handleRemoveUri(uri.id)} aria-label='Remove URI'>
              <i className='bi bi-x-lg' />
            </BsButton>
          ) : null}
          <Form.Control.Feedback type='invalid'>Please enter a valid URI.</Form.Control.Feedback>
        </InputGroup>
      ))}

      {validated && uris.every((uri) => uri.value.trim() === '') ? <div className='text-danger mb-2 small'>At least one redirect URI is required.</div> : null}

      <BsButton variant='link' className='text-decoration-none d-flex align-items-center gap-1 ps-0 mt-1' onClick={handleAddUri}>
        <i className='bi bi-plus-circle' />
        Add URI
      </BsButton>
    </>
  )
}
